<template>
  <div class="notification-bell">
    <el-popover
      v-model="visible"
      placement="bottom-end"
      width="360"
      trigger="click"
      popper-class="notification-popover"
    >
      <!-- 触发按钮 -->
      <div slot="reference" class="bell-wrapper" @click="handleBellClick">
        <i class="el-icon-bell"></i>
        <el-badge 
          v-if="unreadCount > 0" 
          :value="unreadCount > 99 ? '99+' : unreadCount" 
          class="notification-badge"
        />
      </div>

      <!-- 消息列表 -->
      <div class="notification-panel">
        <!-- 头部 -->
        <div class="notification-header">
          <span class="title">
            <i class="el-icon-bell"></i>
            消息通知
            <el-badge v-if="unreadCount > 0" :value="unreadCount" class="header-badge"/>
          </span>
          <div class="actions">
            <el-button type="text" size="mini" @click="markAllRead">
              全部已读
            </el-button>
            <el-button type="text" size="mini" @click="goToMessageCenter">
              更多
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>

        <!-- 消息列表内容 -->
        <div class="notification-list" v-loading="loading">
          <template v-if="messageList.length > 0">
            <div
              v-for="msg in messageList"
              :key="msg.id"
              class="notification-item"
              :class="{ unread: !msg.isRead }"
              @click="handleMessageClick(msg)"
            >
              <div class="item-icon" :class="getIconClass(msg.type)">
                <i :class="getIcon(msg.type)"></i>
              </div>
              <div class="item-content">
                <div class="item-title">{{ msg.title }}</div>
                <div class="item-desc">{{ msg.content }}</div>
                <div class="item-time">{{ formatTime(msg.createTime) }}</div>
              </div>
              <div v-if="!msg.isRead" class="unread-dot"></div>
            </div>
          </template>
          <el-empty v-else description="暂无消息" :image-size="60"></el-empty>
        </div>

        <!-- 底部 -->
        <div class="notification-footer">
          <el-button type="text" size="small" @click="refreshMessages">
            <i class="el-icon-refresh"></i> 刷新
          </el-button>
          <span class="footer-text">共 {{ total }} 条消息</span>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script>
import request from '@/utils/request'
export default {
  name: 'NotificationBell',
  data() {
    return {
      visible: false,
      loading: false,
      unreadCount: 0,
      total: 0,
      messageList: [],
      timer: null
    }
  },
  mounted() {
    this.fetchUnreadCount()
    // 每30秒刷新一次未读数
    this.timer = setInterval(() => {
      this.fetchUnreadCount()
    }, 30000)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    // 获取未读消息数
    async fetchUnreadCount() {
      try {
        const res = await request.get('/notification/unread-count', { _silent: true })
        if (res.data.code === 200) {
          this.unreadCount = res.data.data
        }
      } catch (e) {
        // 后端通知服务未部署，静默忽略
        if (e.response && e.response.status !== 404) {
          console.error('获取未读消息数失败', e)
        }
      }
    },
    // 获取消息列表
    async fetchMessageList() {
      this.loading = true
      try {
        const res = await this.$http.get('/api/notification/list', {
          params: { page: 1, size: 10 }
        })
        if (res.data.code === 200) {
          this.messageList = res.data.data.list || []
          this.total = res.data.data.total || 0
        }
      } catch (e) {
        if (e.response && e.response.status !== 404) {
          console.error('获取消息列表失败', e)
          this.$message.error('获取消息列表失败')
        }
      } finally {
        this.loading = false
      }
    },
    // 点击铃铛
    handleBellClick() {
      if (this.visible) {
        this.fetchMessageList()
      }
    },
    // 点击消息
    async handleMessageClick(msg) {
      if (!msg.isRead) {
        await this.markAsRead(msg.id)
      }
      // 根据消息类型跳转
      this.handleMessageRoute(msg)
      this.visible = false
    },
    // 标记单条已读
    async markAsRead(id) {
      try {
        await this.$http.post('/api/notification/read', { id })
        const msg = this.messageList.find(m => m.id === id)
        if (msg) {
          msg.isRead = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }
      } catch (e) {
        if (e.response && e.response.status !== 404) {
          console.error('标记已读失败', e)
        }
      }
    },
    // 标记全部已读
    async markAllRead() {
      try {
        await this.$http.post('/api/notification/read-all')
        this.messageList.forEach(msg => msg.isRead = true)
        this.unreadCount = 0
        this.$message.success('已全部标记为已读')
      } catch (e) {
        if (e.response && e.response.status !== 404) {
          console.error('标记全部已读失败', e)
          this.$message.error('操作失败')
        }
      }
    },
    // 刷新消息
    refreshMessages() {
      this.fetchMessageList()
      this.fetchUnreadCount()
    },
    // 跳转到消息中心
    goToMessageCenter() {
      this.$router.push('/message/center')
      this.visible = false
    },
    // 根据消息类型获取图标
    getIcon(type) {
      const icons = {
        'system': 'el-icon-s-tools',
        'order': 'el-icon-s-order',
        'notice': 'el-icon-s-promotion',
        'warning': 'el-icon-warning',
        'property': 'el-icon-office-building',
        'merchant': 'el-icon-s-shop',
        'owner': 'el-icon-user'
      }
      return icons[type] || 'el-icon-bell'
    },
    // 根据消息类型获取图标样式
    getIconClass(type) {
      return `icon-${type}`
    },
    // 处理消息跳转
    handleMessageRoute(msg) {
      if (msg.linkUrl) {
        if (msg.linkUrl.startsWith('http')) {
          window.open(msg.linkUrl, '_blank')
        } else {
          this.$router.push(msg.linkUrl)
        }
      }
    },
    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date
      
      // 1分钟内
      if (diff < 60000) return '刚刚'
      // 1小时内
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      // 24小时内
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      // 7天内
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
      
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.notification-bell {
  display: inline-block;
}

.bell-wrapper {
  position: relative;
  cursor: pointer;
  padding: 8px;
  font-size: 18px;
  color: #606266;
  transition: color 0.3s;
}

.bell-wrapper:hover {
  color: #409EFF;
}

.notification-badge {
  position: absolute;
  top: 0;
  right: 0;
}

.notification-badge >>> .el-badge__content {
  border: none;
}

/* 消息面板样式 */
.notification-panel {
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
}

.notification-header .title {
  font-weight: 600;
  font-size: 15px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-header .title i {
  color: #409EFF;
}

.notification-header .actions {
  display: flex;
  gap: 12px;
}

.header-badge >>> .el-badge__content {
  background-color: #f56c6c;
}

/* 消息列表 */
.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
}

.notification-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.notification-item:hover {
  background-color: #f5f7fa;
}

.notification-item.unread {
  background-color: #f0f9ff;
}

.notification-item.unread:hover {
  background-color: #e6f7ff;
}

.item-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.item-icon i {
  font-size: 18px;
  color: #fff;
}

/* 不同类型图标颜色 */
.icon-system { background-color: #909399; }
.icon-order { background-color: #409EFF; }
.icon-notice { background-color: #67C23A; }
.icon-warning { background-color: #E6A23C; }
.icon-property { background-color: #00a8a8; }
.icon-merchant { background-color: #c41e3a; }
.icon-owner { background-color: #722ed1; }

.item-content {
  flex: 1;
  min-width: 0;
}

.item-title {
  font-weight: 500;
  color: #303133;
  font-size: 14px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-desc {
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 4px;
}

.item-time {
  color: #909399;
  font-size: 12px;
}

.unread-dot {
  width: 8px;
  height: 8px;
  background-color: #f56c6c;
  border-radius: 50%;
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
}

/* 底部 */
.notification-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid #ebeef5;
  background-color: #fafafa;
}

.footer-text {
  font-size: 12px;
  color: #909399;
}
</style>

<style>
/* 覆盖popover样式 */
.notification-popover {
  padding: 0 !important;
}

.notification-popover .el-popover__title {
  display: none;
}
</style>
